import type { FolderDto, FolderKind } from '@tms/shared';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button, Dialog, Field, SelectMenu, SelectMenuItem } from '../../components/ui';
import { useErrorText, useT } from '../../i18n';
import { flattenFolders, useDeleteFolder, useFolderTree } from './api';

export interface DeleteFolderDialogProps {
  projectCode: string;
  /** Which tree the folder is in — it decides what "contents" means here. */
  kind: FolderKind;
  /** The folder being deleted, or `null` when the dialog is closed. */
  folder: FolderDto | null;
  onClose: () => void;
  onDeleted: (folder: FolderDto) => void;
}

/** Ids of a folder and everything under it — never a destination for its cases. */
const subtreeIds = (folder: FolderDto): string[] => [
  folder.id,
  ...folder.children.flatMap(subtreeIds),
];

/**
 * Deleting a folder has to answer one question first: what happens to the test
 * cases or checklists inside it. They cannot be left without a folder, so the
 * dialog either takes a destination for them or deletes them along with it.
 */
export const DeleteFolderDialog = ({
  projectCode,
  kind,
  folder,
  onClose,
  onDeleted,
}: DeleteFolderDialogProps) => {
  const { t } = useT();
  const { errorText } = useErrorText();
  const { data: folders } = useFolderTree(projectCode, kind);
  const remove = useDeleteFolder(projectCode, kind);
  const [targetFolderId, setTargetFolderId] = useState('');

  const contentCount = folder?.totalCount ?? 0;

  const destinations = useMemo(() => {
    if (!folder) return [];
    const excluded = new Set(subtreeIds(folder));
    return flattenFolders(folders ?? []).filter((option) => !excluded.has(option.id));
  }, [folder, folders]);

  useEffect(() => {
    // Default to the parent folder when there is one — the nearest home.
    const parent = destinations.find((option) => option.id === folder?.parentId);
    setTargetFolderId(parent?.id ?? destinations[0]?.id ?? '');
  }, [folder, destinations]);

  const submit = async (strategy: 'move' | 'cascade') => {
    if (!folder) return;
    try {
      await remove.mutateAsync({
        folderId: folder.id,
        strategy,
        ...(strategy === 'move' && targetFolderId ? { targetFolderId } : {}),
      });
      toast.success(t('folders.deleted'));
      onDeleted(folder);
      onClose();
    } catch (error) {
      toast.error(errorText(error, 'folders.deleteFailed'));
    }
  };

  const keepPossible = contentCount > 0 && destinations.length > 0;

  /** "3 test cases", "2 checklists" or "5 runs", depending on the tree. */
  const contents =
    kind === 'case'
      ? t('projects.countCases', { count: contentCount })
      : kind === 'checklist'
        ? t('checklists.countChecklists', { count: contentCount })
        : t('projects.countRuns', { count: contentCount });

  /**
   * A run is the record of an execution: it outlives the folder it was filed
   * in, so this tree offers moving only — and nothing at all when there is
   * nowhere to move to.
   */
  const keepsContents = kind === 'run' && contentCount > 0;

  return (
    <Dialog
      open={folder !== null}
      onOpenChange={(open) => !open && onClose()}
      title={t('folders.deleteTitle', { name: folder?.name ?? '' })}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={remove.isPending}>
            {t('common.cancel')}
          </Button>
          {contentCount > 0 && !keepsContents ? (
            <Button
              variant="danger"
              onClick={() => void submit('cascade')}
              loading={remove.isPending}
            >
              {t('folders.deleteWithCases')}
            </Button>
          ) : null}
          {contentCount === 0 || keepPossible ? (
            <Button
              variant={contentCount === 0 ? 'danger' : 'primary'}
              onClick={() => void submit(contentCount === 0 ? 'cascade' : 'move')}
              loading={remove.isPending}
              disabled={keepPossible && targetFolderId === ''}
            >
              {contentCount === 0 ? t('folders.delete') : t('folders.deleteMoveCases')}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-[13px] text-ink-muted">
          {contentCount === 0
            ? t('folders.deleteHint')
            : keepPossible
              ? t('folders.deleteHintCases', { content: contents })
              : keepsContents
                ? t('folders.deleteBlockedRuns', { content: contents })
                : t('folders.deleteHintOnlyFolder', { content: contents })}
        </p>

        {keepPossible ? (
          <Field label={t('folders.deleteMoveTo')}>
            {({ id }) => (
              <SelectMenu id={id} value={targetFolderId} onValueChange={setTargetFolderId}>
                {destinations.map((option) => (
                  <SelectMenuItem key={option.id} value={option.id}>
                    {option.label}
                  </SelectMenuItem>
                ))}
              </SelectMenu>
            )}
          </Field>
        ) : null}
      </div>
    </Dialog>
  );
};
